/**
 * @retail-ops/shared-types/config/call-class-map
 *
 * CustomerType → AcctClsfCode mapping. Single source of truth ported from
 * the SPFx canonical config (`spfx-account-opening-v2/src/webparts/
 * accountOpeningV2/models/types.ts:91-99` `CALL_CLASS_MAP`) and
 * cross-validated against:
 *   - `Docs/ENTITY-TYPE-PROCEDURE-ANALYSIS.md` §Call Class Master Reference
 *   - Live `Parameter/ParmValSrch?ParmName=AcctClsfCode` (28 records)
 *   - Production data observation across 33,067 active DDA accounts
 *
 * The AcctClsfCode value sent on AcctAdd at runtime derives from the
 * primary owner's CustomerType. Per-product `allowedCallClasses` (from
 * `dbo.ProductAllowedCallClasses`) gates which products can be opened
 * for which entity types; this map turns "primary is `RevocableTrust`"
 * into "AcctClsfCode = 30" so the gating predicate can compare strings.
 *
 * Keep in lockstep with the SPFx file. Diverging breaks the gating
 * predicate (a product whose allow-set is `{20, 22}` should hide for an
 * Individual cart only if Individual maps to `0` — if a typo says it
 * maps to `20`, the predicate accepts it incorrectly).
 */

import type { CustomerType } from '../enums.js';

/**
 * Canonical CustomerType → AcctClsfCode (a.k.a. CallClass) string code.
 *
 * Aligned with PCB's live `dbo.AcctClsfCode` enum (probed 2026-05-04):
 *   0 Personal | 1 Conservator | 2 Rep Payee | 3 Foreign/Non-US Citizen |
 *   6 UTMA | 7 VA Fiduciary | 8 Medical Set-Aside | 12 USDA Grant |
 *   14 Individual Shadow | 20 Business | 21 Non Profit | 22 Public Fund |
 *   23 MO Lawyer Trust | 24 Non Grantor Trusts | 25 Business DBA |
 *   26 Personal DBA | 27 Estate | 28 Political | 29 Business Shadow |
 *   30 Grantor Trust (Personal) | 31 Qualified Income Trust |
 *   32 Donation Acct-Short Term
 *
 * Codes 4, 5, 9, 10, 11, 13 are "DO NOT USE" / institution-internal —
 * not in the public CustomerType domain.
 */
export const CALL_CLASS_MAP: Readonly<Record<CustomerType, string>> = {
  // ── Canonical CustomerType set ────────────────────────────────────
  Individual: '0',
  JointTenant: '0',           // joint-tenant carts use the primary's call class
  Minor: '0',                 // Minor primary = Individual call-class; age constraint applies separately
  NonResidentAlien: '3',      // Foreign/Non-US Citizen
  Conservator: '1',
  RepPayee: '2',
  VAFiduciary: '7',
  Estate: '27',
  RevocableTrust: '30',       // Grantor Trust (Personal)
  IrrevocableTrust: '24',     // Non-Grantor Trust
  Corporation: '20',
  LLC: '20',
  Partnership: '20',
  DBA: '26',                  // Personal DBA / Sole Prop is 25 — DBA picker collapses both into 26 by default
  NonProfit: '21',

  // ── Wave 1 #2.7 expansions ─────────────────────────────────────────
  UnincorpAssoc: '21',        // Treated as Non-Profit-class per legacy procedure §18
  IOLTA: '23',
  Political: '28',
  ICS_Business: '29',
  QIT: '31',                  // Qualified Income Trust (Account)
  SelfSettledSNT: '24',       // Categorized with Non-Grantor Trusts
  MedicalSetAside: '8',
  USDAGrant: '12',
  Donation: '32',
  ICS_Personal: '14',

  // ── Legacy aliases (kept for v1/v2 compat — DO NOT use in new code)
  SoleProp: '25',             // Business DBA
  PublicFund: '22',
  Trust: '30',                // generic Trust → defaults to Grantor; explicit subtype required
  Conservatorship: '1',
  IRA: '0',                   // IRA owner is the Individual
  UGMA: '6',                  // Uniform Gifts (legacy alias of UTMA)
  UTMA: '6',
  LLP: '20',
  LLLP: '20',
  LimitedPartnership: '20',
  GeneralPartnership: '20',
  Church: '21',               // Religious non-profit
  Association: '21',
  Government: '22',           // Public Fund umbrella
  SchoolDistrict: '22',       // Public Fund (school)
  MinorAccount: '0',
  Other: '0',                 // last-resort fallback to Individual
};

/** The shape `callClassFor` needs from a configured entity rule. */
export interface CallClassConfigRule {
  readonly customerType?: string;
  readonly callClassCode?: string | null;
}

/**
 * Resolve the AcctClsfCode for a given CustomerType.
 *
 * CONFIG FIRST, MAP AS FALLBACK (Bill 2026-09-23). Until now this map was the
 * only source, which put the value that gates products and travels to Core on
 * the wrong side of a deploy: `EntityRules.CallClassCode` was editable in the
 * Compliance panel and published on /v3/config/entity-rules, but nothing read
 * it — so Compliance could change a classification and have it do nothing,
 * while the live value could only move with a build of this package. That is
 * the inverse of how everything else here works.
 *
 * So a configured rule wins, and this map is the fallback for when config has
 * not loaded, has no row for the type, or leaves the code blank. The two were
 * reconciled in seed 221 (15 rows corrected to match this map, values copied
 * rather than invented), so today they agree and the fallback is invisible.
 *
 * Returns `'0'` (Individual) as the last resort. Callers needing to detect
 * "unmapped" should check `customerType in CALL_CLASS_MAP` first.
 *
 * NOTE THE DOMAIN: this is an **AcctClsfCode** (account classification, 0-32),
 * NOT Core's CIF **CallClass** (0-31). Different enums, overlapping numbers,
 * different meanings — 20 is 'Business' here and 'Partnership' there.
 */
let registeredRules: readonly CallClassConfigRule[] | undefined;

/**
 * Hand the loaded entity rules to `callClassFor`, once, when config arrives.
 *
 * Module state rather than a parameter, deliberately. `callClassFor` has five
 * call sites across three files, several of them inside pure predicates with
 * no access to React context. Threading config through all of them would mean
 * a new caller who forgets the argument silently falls back to the map — which
 * is precisely the failure this change exists to remove. Registering once
 * makes the configured value the default and the map the exception.
 *
 * The value is loaded once at startup and read-only thereafter, so the usual
 * objections to module state do not bite here. Pass `configuredRules`
 * explicitly to override it in a test.
 */
export function registerCallClassRules(rules: readonly CallClassConfigRule[] | undefined): void {
  registeredRules = rules?.length ? rules : undefined;
}

export function callClassFor(
  customerType: CustomerType | string | undefined,
  configuredRules?: readonly CallClassConfigRule[],
): string {
  if (!customerType) return '0';

  const rules = configuredRules ?? registeredRules;
  if (rules?.length) {
    const row = rules.find((r) => r.customerType === customerType);
    const configured = (row?.callClassCode ?? '').trim();
    // A blank code is "not configured", not a deliberate empty — fall through
    // rather than sending '' to Core as a classification.
    if (configured) return configured;
  }

  const mapped = (CALL_CLASS_MAP as Record<string, string>)[customerType];
  return mapped ?? '0';
}
